import type { IconDefinition } from '@ant-design/icons-angular';

import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import en from '@angular/common/locales/en';
import { DOCUMENT, inject, isDevMode, NgModule, provideAppInitializer, provideEnvironmentInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AlertOutline,
  EyeInvisibleOutline,
  FileTwoTone,
  LoadingOutline,
  LockOutline,
  MenuOutline,
  MobileOutline,
  PlusOutline,
  SmileTwoTone,
  UserOutline,
} from '@ant-design/icons-angular/icons';
import { provideLoadingService } from '@eliboo/angular/loading';
import { microApiInterceptor, microAuthSimpleInterceptor } from '@eliboo/micro-ng/http/interceptor';
import { provideGlobalErrorHandler } from '@eliboo/shared/error-handler';
import { flexible } from '@eliboo/web-kit';
import { WEB_ENVIRONMENT } from '@env';
import { WA_USER_AGENT } from '@ng-web-apis/common';
import { TUI_PLATFORM } from '@taiga-ui/cdk';
import { provideEventPlugins } from '@taiga-ui/event-plugins';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzAnchorModule } from 'ng-zorro-antd/anchor';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';

import { environment } from '../environments/environment';
import { ElibooLoadingTUIComponent } from '../shared/loading';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './core/api/app.service';
import { ShareModule } from './share/share.module';
const icons: IconDefinition[] = [
  MenuOutline,
  UserOutline,
  LockOutline,
  EyeInvisibleOutline,
  PlusOutline,
  LoadingOutline,
  FileTwoTone,
  SmileTwoTone,
  AlertOutline,
  MobileOutline,
];
const elibooProvides = [
  { provide: NZ_I18N, useValue: en_US },
  { provide: WEB_ENVIRONMENT, useValue: environment },
];
registerLocaleData(en);

function setFlexible(document: Document) {
  let destroySecond = 5;
  if (isDevMode()) {
    destroySecond = 0;
    // 开发环境下不设置flexible
    const userAgent = inject(WA_USER_AGENT);
    const isMobile = /mobile|iphone/i.test(userAgent);
    console.log('userAgent', userAgent, isMobile);
    if (!isMobile) {
      if (['Chrome', 'Edg'].some((agent) => userAgent.includes(agent))) return;
    }
  }
  flexible(window, document, 16, 375, destroySecond);
}
@NgModule({
  declarations: [
    AppComponent,
    // CollaborativeComponent,
    // ImagesComponent,
    // HeadComponent,
  ],
  imports: [
    CommonModule,
    NzSelectModule,
    NzRadioModule,
    NzAnchorModule,
    NzFormModule,
    NzAffixModule,
    NzIconModule.forRoot(icons),
    NzDropDownModule,
    NzButtonModule,
    NzCarouselModule,
    BrowserModule,
    AppRoutingModule,
    ShareModule,
    FormsModule,

    BrowserAnimationsModule,
    NzModalModule,
    NzInputModule,
    NzTableModule,
  ],
  providers: [
    ...elibooProvides,
    provideAppInitializer(() => inject(AppService).init(true)),
    provideHttpClient(withInterceptors([microAuthSimpleInterceptor, microApiInterceptor])),
    provideEnvironmentInitializer(() => {
      const document = inject(DOCUMENT);
      setFlexible(document);
      document.body.setAttribute('data-platform', inject(TUI_PLATFORM));
    }),
    provideLoadingService(ElibooLoadingTUIComponent),
    provideEventPlugins(),
    // 全局错误处理器
    provideGlobalErrorHandler({ enableConsoleLog: !environment.production }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
